import { useCallback, useMemo } from "react";
import { SessionHandler } from "verdi-web-client";
import { useListDataStreams } from "@/api/orval/generated/data-stream";
import type { ListDataStreams200Item, ListDataStreamsParams } from "@/api/orval/generated/schemas";
import { useListZones } from "@/api/orval/generated/zone";
import { type DashboardSpec, type PanelSpec, SPEC_VERSION, type StreamEntry } from "../ChartSpec";
import { DEFAULT_RELATIVE_DURATION } from "../time-window/time-range-slice";
import {
    buildAllStreamsPanel,
    buildRecipePanels,
    irrigationBand,
    type PanelRecipe,
    toStreamEntries,
} from "./panel-builders";
import {
    DEVICE_CARD_RELATION,
    isPlottableStream,
    type ResolvedStream,
    resolveStreams,
    toResolvedStream,
    type ZoneNameLookup,
    zoneCardRelation,
} from "./resolve-streams";
import { selectStreams } from "./select-streams";
import { withoutLeftoverStreams } from "./stream-list-workarounds";
import { type StreamVisibility, visibleUpTo } from "./stream-visibility";
import type { UnitCategoryPresentation } from "./unit-category-presentation";
import { useOrgDataStreams } from "./use-org-data-streams";

const DEFAULT_TIME_RANGE = { mode: "relative", duration: DEFAULT_RELATIVE_DURATION } as const;

const NO_ADDED_IDS: readonly number[] = Object.freeze([]);

/** Rain read against temperature, and a gust read against the wind speed it peaked over. */
const SHARED_PANEL_RECIPES: PanelRecipe[] = [
    { left: ["airTemperature"], right: "precipitation" },
    { left: [["windSpeed", "windGust"]] },
];

function withSharedRecipes(panelRecipes: PanelRecipe[] = []): PanelRecipe[] {
    return [...SHARED_PANEL_RECIPES, ...panelRecipes];
}

/**
 * "stacked" is a panel per recipe the card asks for, then a panel per unit category; "single" is
 * every stream on one panel.
 */
export type PanelLayout = "single" | "stacked";

/**
 * What the card putting the viewer on screen asks the generator for. Every field is an opinion the
 * card holds about its own metrics, so leaving one out is not a lesser viewer — it is a card with
 * nothing to say about that, and the generator falls back to the order the API sent and the
 * palette in that order. Admin-only streams are left out for non-admins whatever the card asks.
 */
export type DataViewerSpecOptions = {
    /** Leave out streams about the device itself (onboard temperature, battery, network stats). */
    hideDiagnostics?: boolean;
    /** Where each category's panel sits in the stack, and what color its series take. */
    unitCategoryPresentation?: UnitCategoryPresentation[];
    /** Categories the card wants drawn on one panel, besides temperature with rain and wind with gust, which every card gets. */
    panelRecipes?: PanelRecipe[];
    /** Scheduled and actual irrigation over the first panel, where the legacy strip sat. Zones only. */
    irrigationBand?: boolean;
};

export type DataViewerSpecResult = {
    spec: DashboardSpec;
    isLoading: boolean;
    isError: boolean;
    isEmpty: boolean;
};

function toSpec(panels: PanelSpec[]): DashboardSpec {
    return { version: SPEC_VERSION, defaultTimeRange: DEFAULT_TIME_RANGE, panels };
}

function zonePanels(entries: StreamEntry[], layout: PanelLayout, panelRecipes: PanelRecipe[]): PanelSpec[] {
    if (layout === "stacked") return buildRecipePanels(entries, panelRecipes);

    const panel = buildAllStreamsPanel(entries);
    return panel ? [panel] : [];
}

export function getZoneSpec(
    zoneId: string,
    resolvedStreams: ResolvedStream[],
    layout: PanelLayout,
    options: DataViewerSpecOptions = {},
): DashboardSpec {
    const panels = zonePanels(
        toStreamEntries(resolvedStreams, options.unitCategoryPresentation ?? []),
        layout,
        withSharedRecipes(options.panelRecipes),
    );
    if (!options.irrigationBand || panels.length === 0) return toSpec(panels);

    const [first, ...rest] = panels;
    return toSpec([{ ...first, bands: [irrigationBand(zoneId, { position: "above" })] }, ...rest]);
}

export function getDeviceSpec(
    resolvedStreams: ResolvedStream[],
    { unitCategoryPresentation = [], panelRecipes }: DataViewerSpecOptions = {},
): DashboardSpec {
    const ownStreams = resolvedStreams.map((stream) => ({ ...stream, cardRelation: DEVICE_CARD_RELATION }));
    return toSpec(
        buildRecipePanels(toStreamEntries(ownStreams, unitCategoryPresentation), withSharedRecipes(panelRecipes)),
    );
}

/**
 * The same options back, holding one reference for as long as the contents match. A card passing a
 * fresh literal every render would otherwise rebuild the spec each time, and a new spec replaces
 * the document — losing the time range and axis the viewer is sitting on.
 */
function useStableOptions(options: DataViewerSpecOptions): DataViewerSpecOptions {
    const key = JSON.stringify(options);
    return useMemo(() => JSON.parse(key) as DataViewerSpecOptions, [key]);
}

/** Zone names for the legend's headings. Loading until the zones arrive, or every stream reads as no zone. */
function useZoneNames(enabled: boolean): { zoneName: ZoneNameLookup; isLoading: boolean } {
    const zones = useListZones({ query: { enabled } });
    const zoneName = useMemo(() => {
        const names = new Map((zones.data ?? []).map((zone) => [zone._id, zone.name]));
        return (zoneId: string) => names.get(zoneId);
    }, [zones.data]);

    return { zoneName, isLoading: zones.isLoading };
}

function viewerVisibleUpTo({ hideDiagnostics = false }: DataViewerSpecOptions): StreamVisibility {
    return visibleUpTo({ hideDiagnostics, isAdmin: Boolean(SessionHandler.admin) });
}

function useResolvedStreams(params: ListDataStreamsParams, enabled: boolean, upTo: StreamVisibility) {
    const query = useListDataStreams(params, { query: { enabled, select: withoutLeftoverStreams } });
    const zones = useZoneNames(enabled);
    // Undefined params hash like `{}`, so a disabled query still returns the picker's org-wide list.
    const data = enabled ? query.data : undefined;
    const isLoading = query.isLoading || zones.isLoading;
    const { isError } = query;

    const resolvedStreams: ResolvedStream[] = useMemo(
        () => (data ? resolveStreams(data, zones.zoneName, upTo) : []),
        [data, zones.zoneName, upTo],
    );

    return {
        resolvedStreams,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
    };
}

/** Think of this as the configuration file for how a zone-based DataViewer should be rendered. */
export function useZoneDataViewerSpec(
    zoneId: string,
    layout: PanelLayout,
    options: DataViewerSpecOptions = {},
    addedIds: readonly number[] = NO_ADDED_IDS,
): DataViewerSpecResult & {
    plottedStreams: ListDataStreams200Item[];
    isPlottable: (stream: ListDataStreams200Item) => boolean;
} {
    const stableOptions = useStableOptions(options);
    const isAdmin = Boolean(SessionHandler.admin);
    const plottableUpTo = visibleUpTo({ hideDiagnostics: false, isAdmin });
    const defaultUpTo = viewerVisibleUpTo(stableOptions);
    const zoneStreams = useListDataStreams(
        { zoneId },
        { query: { enabled: !!zoneId, select: withoutLeftoverStreams } },
    );
    const zoneData = zoneStreams.data;
    const zones = useZoneNames(!!zoneId);
    const isLoading = zoneStreams.isLoading || zones.isLoading;
    const { isError } = zoneStreams;
    const org = useOrgDataStreams(addedIds.length > 0);

    // Disabled, but still gets the picker's cached data; reading it would rebuild the spec for nothing.
    const orgData = addedIds.length > 0 ? org.data : undefined;

    // Hidden-by-default streams count as plottable, so the picker can offer them and an added one draws.
    const isPlottable = useCallback(
        (stream: ListDataStreams200Item) => isPlottableStream(stream, plottableUpTo),
        [plottableUpTo],
    );
    const selected = useMemo(() => {
        const shownByDefault = (zoneData ?? []).filter((stream) => isPlottableStream(stream, defaultUpTo));
        return selectStreams(shownByDefault, orgData ?? [], addedIds).filter(isPlottable);
    }, [zoneData, orgData, addedIds, isPlottable, defaultUpTo]);
    const resolvedStreams = useMemo(() => {
        const zoneStreamIds = new Set((zoneData ?? []).map((stream) => stream.id));
        return selected.map((stream) => ({
            ...toResolvedStream(stream, zones.zoneName),
            cardRelation: zoneCardRelation(stream, zoneId, zoneStreamIds.has(stream.id), zones.zoneName),
        }));
    }, [selected, zoneData, zoneId, zones.zoneName]);

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(
        () => getZoneSpec(zoneId, resolvedStreams, layout, stableOptions),
        [zoneId, resolvedStreams, layout, stableOptions],
    );

    return {
        spec,
        isLoading,
        isError,
        isEmpty: !isLoading && !isError && resolvedStreams.length === 0,
        plottedStreams: selected,
        isPlottable,
    };
}

/**
 * Think of this as the configuration file for how a device-based DataViewer should be rendered.
 *
 * @param host - The host device that owns the streams. The streams API rejects a device id without its
 *     collection, so pass both or nothing.
 */
export function useDeviceDataViewerSpec(
    host: { id: string; collection: string } | undefined,
    options: DataViewerSpecOptions = {},
): DataViewerSpecResult {
    const stableOptions = useStableOptions(options);
    const { resolvedStreams, ...status } = useResolvedStreams(
        { deviceId: host?.id, deviceCollection: host?.collection },
        !!host,
        viewerVisibleUpTo(stableOptions),
    );

    // SpecProvider replaces the document when initialSpec's reference changes — keep it stable.
    const spec = useMemo(() => getDeviceSpec(resolvedStreams, stableOptions), [resolvedStreams, stableOptions]);

    return { spec, ...status };
}
